import CheckIcon from "@mui/icons-material/Check";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { type FC, type MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import dlrLogo from "src/assets/dlr-logo.png";
import { dxmSsoConfig } from "src/auth/dxmSsoConfig";
import { SignOutButton } from "src/auth/SignOutButton";
import { usePermissions } from "src/auth/usePermissions";
import { useSession } from "src/auth/useSession";
import { TOP_BAR_HEIGHT } from "src/components/AppShell/layout";
import { userInitials } from "src/components/AppShell/userInitials";
import { NotificationBell } from "src/notifications/NotificationBell";
import { ROUTES } from "src/routes/paths";
import { SolutionPill } from "src/workspace/SolutionPill";

/** The sign-in page's logo size on desktop (28px tall); a little smaller in the phone-height bar. */
const LOGO_WIDTH = { xs: 105, md: 123 } as const;
const AVATAR_SIZE = 30;
const MENU_MIN_WIDTH = 240;

interface OrganizationMenuItemProps {
	organization: { id: string; name: string };
	active: boolean;
	onSelect: (organizationId: string) => void;
}

const OrganizationMenuItem: FC<OrganizationMenuItemProps> = ({ organization, active, onSelect }) => {
	const handleClick = useCallback(() => onSelect(organization.id), [onSelect, organization.id]);
	return (
		<MenuItem selected={active} onClick={handleClick}>
			<ListItemText>{organization.name}</ListItemText>
			{active && (
				<ListItemIcon>
					<CheckIcon fontSize="small" color="primary" />
				</ListItemIcon>
			)}
		</MenuItem>
	);
};

/**
 * Brand on the left, the notification bell and account on the right (Figma "Top bar" / "App header",
 * "Notification + Account"). The account menu holds what the wireframes keep out of the navigation:
 * the organization switcher, members, notification settings, the admin console (API tokens among its
 * pages; shown only to roles holding one of its pages' permissions) and sign out.
 */
export const AppTopBar: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user, organizations, activeOrganization, isPartner, switchOrganization } = useSession();
	const { canSeeMembers, canOpenAdmin } = usePermissions();
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);

	const openMenu = useCallback((event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget), []);
	const closeMenu = useCallback(() => setAnchor(null), []);
	const handleOrganization = useCallback(
		async (organizationId: string) => {
			setAnchor(null);
			if (organizationId === activeOrganization?.id) return;
			await switchOrganization(organizationId);
			await navigate(ROUTES.home);
		},
		[activeOrganization?.id, navigate, switchOrganization]
	);
	const openMembers = useCallback(async () => {
		setAnchor(null);
		await navigate(ROUTES.members);
	}, [navigate]);
	const openNotificationSettings = useCallback(async () => {
		setAnchor(null);
		await navigate(ROUTES.notificationSettings);
	}, [navigate]);
	const openAdmin = useCallback(async () => {
		setAnchor(null);
		await navigate(ROUTES.admin);
	}, [navigate]);

	if (!user) return null;

	return (
		<AppBar position="sticky">
			<Toolbar sx={{ minHeight: TOP_BAR_HEIGHT, px: { xs: 2, md: 3 }, gap: { xs: 1.25, md: 1.5 } }} disableGutters>
				<Box
					component={Link}
					to={ROUTES.home}
					// Shrinks before anything overflows: the title truncates on the narrowest phones, where the
					// bell and the avatar still need their room.
					sx={{
						display: "flex",
						alignItems: "center",
						gap: { xs: 1.25, md: 1.5 },
						minWidth: 0,
						textDecoration: "none"
					}}>
					<Box
						component="img"
						src={dlrLogo}
						alt="Digital Realty"
						sx={{ width: LOGO_WIDTH, height: "auto", display: "block", flexShrink: 0 }}
					/>
					<Typography component="span" color="primary" noWrap sx={{ fontSize: { xs: 16, md: 20 }, fontWeight: 500 }}>
						{t("app.title")}
					</Typography>
				</Box>
				<Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0, px: { xs: 0, sm: 2 } }}>
					<SolutionPill />
				</Box>
				{isPartner && activeOrganization && (
					<Typography
						variant="body2"
						color="text.secondary"
						noWrap
						sx={{ display: { xs: "none", lg: "block" }, maxWidth: 240 }}>
						{activeOrganization.name}
					</Typography>
				)}
				<NotificationBell />
				<IconButton onClick={openMenu} aria-label={t("nav.account")} aria-haspopup="menu" sx={{ p: 0.5 }}>
					<Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}>{userInitials(user)}</Avatar>
				</IconButton>
				<Menu
					anchorEl={anchor}
					open={Boolean(anchor)}
					onClose={closeMenu}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{ paper: { sx: { minWidth: MENU_MIN_WIDTH } } }}>
					<Box sx={{ px: 2, py: 1 }}>
						<Typography variant="subtitle2">{user.name ?? user.email}</Typography>
						{user.name && user.email && user.name !== user.email && (
							<Typography variant="caption" color="text.secondary">
								{user.email}
							</Typography>
						)}
					</Box>
					<Divider />
					{organizations.length > 1 && <ListSubheader>{t("nav.switchTo")}</ListSubheader>}
					{organizations.length > 1 &&
						organizations.map(organization => (
							<OrganizationMenuItem
								key={organization.id}
								organization={organization}
								active={organization.id === activeOrganization?.id}
								onSelect={handleOrganization}
							/>
						))}
					{organizations.length > 1 && <Divider />}
					{isPartner && canSeeMembers && <MenuItem onClick={openMembers}>{t("nav.members")}</MenuItem>}
					<MenuItem onClick={openNotificationSettings}>{t("notifications.settings.title")}</MenuItem>
					{canOpenAdmin && <MenuItem onClick={openAdmin}>{t("nav.admin")}</MenuItem>}
					<SignOutButton withDxmSso={Boolean(dxmSsoConfig)} asMenuItem />
				</Menu>
			</Toolbar>
		</AppBar>
	);
};
